import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { BreadcrumbService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Business } from '@app/models/business';
import {
  Illumination,
  IlluminationLabels,
  LabTestingParameterType,
  LabTestingResult,
  LabTestingResultParameter,
  LabTestingStandard,
  LabTestingStandardParameter,
  TestGroup,
} from '@app/models/lab-testing';
import { WorkOrder } from '@app/models/order/work-order';
import { ProductionCard, ProductionCardSearchCommand } from '@app/models/production/production-card';
import { FinishingRoles } from '@app/models/roles/roles-group';
import { BusinessProxyService } from '@app/proxies/finance/business.proxy.service';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { LabTestingResultProxyService } from '@app/proxies/production/lab-testing-result.proxy.service';
import { LabTestingStandardProxyService } from '@app/proxies/production/lab-testing-standard.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { IndividualProductionCardService } from '@app/services/individual-production-card.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-laboratory-tests',
  templateUrl: './laboratory-tests.component.html',
  styleUrl: './laboratory-tests.component.scss',
})
export class LaboratoryTestsComponent implements OnInit {
  private readonly productionCardProxy = inject(ProductionCardsProxyService);
  private readonly workOrderProxy = inject(WorkOrderProxyService);
  private readonly businessProxy = inject(BusinessProxyService);
  private readonly labTestingResultProxy = inject(LabTestingResultProxyService);
  private readonly labTestingStandardProxy = inject(LabTestingStandardProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly individualProductionCardService = inject(IndividualProductionCardService);
  private readonly localeHelperService = inject(LocaleHelperService);

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  saveMenuItems: MenuItem[] = [];

  isLoading = signal(0);

  productionCard = signal<ProductionCard | null>(null);
  workOrder = signal<WorkOrder | null>(null);
  customer = signal<Business | null>(null);

  standards = signal<LabTestingStandard[]>([]);
  selectedStandard = new FormControl<LabTestingStandard | null>(null);

  testResults = signal<LabTestingResult[]>([]);

  isCreating = signal(false);
  selectedStandardForTest = signal<LabTestingStandard | null>(null);

  newTestComposition = new FormControl('');

  /** Plain object, not a signal: actual values change on every keystroke and don't need to trigger re-renders. */
  formData = {
    width: '',
    weight: '',
    shrinkageLength: '',
    shrinkageWidth: '',
    torque: '',
    cpi: '',
    wpi: '',
    bursting: '',
    pilling: '',
    ph: '',
    waterFastness: '',
    perspirationFastness: '',
    crockingDry: '',
    crockingWet: '',
    illuminationColors: null as number | null,
    illuminationWhites: null as number | null,
  };

  illuminationOptions = Object.values(Illumination)
    .filter((v) => typeof v === 'number')
    .map((value) => ({
      label: IlluminationLabels[value as Illumination],
      value: value as number,
    }));

  parameterTypes = Object.values(LabTestingParameterType).filter((v) => typeof v === 'number') as LabTestingParameterType[];

  groupedTests = computed<TestGroup[]>(() => {
    const results = this.testResults();
    const grouped = _.groupBy(results, 'labTestingStandardReference');

    return Object.entries(grouped).map(([standardRef, tests]) => {
      const standard = this.standards().find((s) => s.id === standardRef);
      return {
        composition: standard?.composition || 'Unknown',
        tests,
      };
    });
  });

  customerName = computed(() => this.customer()?.name || '');

  colorName = computed(() => this.productionCard()?.colorReference || '');

  private breadcrumbService = inject(BreadcrumbService);

  ngOnInit(): void {
    this.setDefaultBreadcrumb();
    this.saveMenuItems = [
      {
        label: this.localeHelperService.translateWithFallback('Lab_Tests_SaveAndClose', 'Save and Close'),
        icon: 'pi pi-save',
        command: () => this.save(true),
      },
    ];
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private setDefaultBreadcrumb(): void {
    this.breadcrumbService.set([{ label: 'Lab_Tests_Breadcrumb_Finishing' }, { label: 'Lab_Tests_Breadcrumb_Title' }]);
  }

  loadStandardsByCustomer(customerReference: string): void {
    this.incrementLoading();
    this.labTestingStandardProxy
      .search({ customerReference })
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.standards.set(response.data);
          } else {
            // No customer-specific standard: fall back to the general ones.
            this.loadGeneralStandards();
          }
        },
        error: () => {
          this.loadGeneralStandards();
        },
      });
  }

  loadGeneralStandards(): void {
    this.incrementLoading();
    this.labTestingStandardProxy
      .getAll()
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.standards.set(response.data.filter((s) => !s.customerReference));
          }
        },
      });
  }

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardProxy.search({ productionCardNumber: query } as ProductionCardSearchCommand).pipe(map(({ data }) => data ?? []));

  onProductionCardSelect(event: AutoCompleteSelectEvent): void {
    const selectedCard = event.value as ProductionCard;
    if (!selectedCard) return;

    this.productionCard.set(selectedCard);
    this.testResults.set([]);

    this.loadTestResults(selectedCard.id!);
    if (selectedCard.workOrderReference) {
      this.loadWorkOrder(selectedCard.workOrderReference);
    }

    if (selectedCard.productionCardNumber) {
      this.initializeProductionCardService(selectedCard.productionCardNumber);
    }
  }

  private initializeProductionCardService(productionCardNumber: string): void {
    this.incrementLoading();
    this.individualProductionCardService
      .initialize(productionCardNumber)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe();
  }

  loadWorkOrder(workOrderReference: string): void {
    this.incrementLoading();
    this.workOrderProxy
      .getById(workOrderReference)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.workOrder.set(response.data);
            if (response.data.customerReference) {
              this.loadCustomer(response.data.customerReference);
              this.loadStandardsByCustomer(response.data.customerReference);
            } else {
              this.loadGeneralStandards();
            }
          }
        },
      });
  }

  loadCustomer(customerReference: string): void {
    this.incrementLoading();
    this.businessProxy
      .getBusinessById(customerReference)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.customer.set(response.data);
          }
        },
        error: () => {
          this.customer.set(null);
        },
      });
  }

  loadTestResults(productionCardReference: string): void {
    this.incrementLoading();
    this.labTestingResultProxy
      .getByProductionCardReference(productionCardReference)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.testResults.set(response.data);
          }
        },
      });
  }

  getStandardParam(type: LabTestingParameterType): LabTestingStandardParameter | undefined {
    return this.selectedStandardForTest()?.parameters?.find((p) => p.parameterType === type);
  }

  // If value, min and max are all absent, the parameter is not applicable and the "Actual" cell is disabled.
  hasStandardValue(type: LabTestingParameterType): boolean {
    const param = this.getStandardParam(type);
    if (!param) return false;

    if (type === LabTestingParameterType.PrimaryIllumination) {
      return (
        (param.illuminationValue?.forColors !== undefined && param.illuminationValue?.forColors !== null) ||
        (param.illuminationValue?.forWhites !== undefined && param.illuminationValue?.forWhites !== null)
      );
    }

    const ev = param.expectedValue;
    if (!ev) return false;

    const hasValue = ev.value !== undefined && ev.value !== null && ev.value !== '';
    const hasMin = ev.min !== undefined && ev.min !== null;
    const hasMax = ev.max !== undefined && ev.max !== null;

    return hasValue || hasMin || hasMax;
  }

  formatStandardValue(type: LabTestingParameterType): string {
    const param = this.getStandardParam(type);
    if (!param) return 'N/A';

    const ev = param.expectedValue;
    if (!ev) return 'N/A';

    const hasValue = ev.value !== undefined && ev.value !== null && ev.value !== '';
    const hasMin = ev.min !== undefined && ev.min !== null;
    const hasMax = ev.max !== undefined && ev.max !== null;

    if (hasValue) {
      return ev.value!;
    }

    if (hasMin && hasMax) {
      return `${ev.min} - ${ev.max}`;
    }

    if (hasMin) {
      return `≥ ${ev.min}`;
    }

    if (hasMax) {
      return `≤ ${ev.max}`;
    }

    return 'N/A';
  }

  formatIlluminationStandard(forColors: boolean): string {
    const param = this.getStandardParam(LabTestingParameterType.PrimaryIllumination);
    if (!param?.illuminationValue) return 'N/A';

    const value = forColors ? param.illuminationValue.forColors : param.illuminationValue.forWhites;
    if (value === undefined || value === null) return 'N/A';

    const option = this.illuminationOptions.find((o) => o.value === value);
    return option?.label || String(value);
  }

  hasIlluminationStandardValue(forColors: boolean): boolean {
    const param = this.getStandardParam(LabTestingParameterType.PrimaryIllumination);
    if (!param?.illuminationValue) return false;

    const value = forColors ? param.illuminationValue.forColors : param.illuminationValue.forWhites;
    return value !== undefined && value !== null;
  }

  startCreateTest(): void {
    const pc = this.productionCard();
    const standard = this.selectedStandard.value;

    if (!pc) return;

    if (!standard) {
      GlobalMessageUtil.error(
        this.localeHelperService.translateWithFallback('Error', 'Error'),
        this.localeHelperService.translateWithFallback('Lab_Tests_SelectStandardFirst', 'Please select a standard first'),
      );
      return;
    }

    this.isCreating.set(true);
    this.selectedStandardForTest.set(standard);
    this.newTestComposition.setValue(standard.composition);
    this.initializeEmptyParameters();

    this.breadcrumbService.set([
      { label: 'Lab_Tests_Breadcrumb_Finishing' },
      { label: 'Lab_Tests_Breadcrumb_Title', command: () => this.cancelCreate() },
      { label: 'Lab_Tests_Breadcrumb_NewTest' },
    ]);
  }

  initializeEmptyParameters(): void {
    this.formData = {
      width: '',
      weight: '',
      shrinkageLength: '',
      shrinkageWidth: '',
      torque: '',
      cpi: '',
      wpi: '',
      bursting: '',
      pilling: '',
      ph: '',
      waterFastness: '',
      perspirationFastness: '',
      crockingDry: '',
      crockingWet: '',
      illuminationColors: null,
      illuminationWhites: null,
    };
    // selectedStandardForTest is set by startCreateTest(), not reset here.
  }

  buildParametersFromForm(): LabTestingResult['parameters'] {
    const standard = this.selectedStandardForTest();

    return this.parameterTypes.map((type) => {
      const standardParam = standard?.parameters?.find((p) => p.parameterType === type);

      const param: LabTestingResultParameter = {
        parameterType: type,
        expectedValue: standardParam?.expectedValue
          ? { ...standardParam.expectedValue }
          : { value: '', min: undefined, max: undefined, unit: '' },
        actualValue: { value: '', min: undefined, max: undefined, unit: '' },
        isWithinSpec: undefined,
      };

      // actualValue.value is typed as string, so every form field is coerced before assignment.
      switch (type) {
        case LabTestingParameterType.WidthInches:
          param.actualValue!.value = String(this.formData.width ?? '');
          break;
        case LabTestingParameterType.WeightGrams:
          param.actualValue!.value = String(this.formData.weight ?? '');
          break;
        case LabTestingParameterType.ShrinkageLength:
          param.actualValue!.value = String(this.formData.shrinkageLength ?? '');
          break;
        case LabTestingParameterType.ShrinkageWidth:
          param.actualValue!.value = String(this.formData.shrinkageWidth ?? '');
          break;
        case LabTestingParameterType.Torque:
          param.actualValue!.value = String(this.formData.torque ?? '');
          break;
        case LabTestingParameterType.CPI:
          param.actualValue!.value = String(this.formData.cpi ?? '');
          break;
        case LabTestingParameterType.WPI:
          param.actualValue!.value = String(this.formData.wpi ?? '');
          break;
        case LabTestingParameterType.Bursting:
          param.actualValue!.value = String(this.formData.bursting ?? '');
          break;
        case LabTestingParameterType.PillingWash:
          param.actualValue!.value = String(this.formData.pilling ?? '');
          break;
        case LabTestingParameterType.PH:
          param.actualValue!.value = String(this.formData.ph ?? '');
          break;
        case LabTestingParameterType.WaterFastness:
          param.actualValue!.value = String(this.formData.waterFastness ?? '');
          break;
        case LabTestingParameterType.PerspirationFastness:
          param.actualValue!.value = String(this.formData.perspirationFastness ?? '');
          break;
        case LabTestingParameterType.CrockingDry:
          param.actualValue!.value = String(this.formData.crockingDry ?? '');
          break;
        case LabTestingParameterType.CrockingWet:
          param.actualValue!.value = String(this.formData.crockingWet ?? '');
          break;
        case LabTestingParameterType.PrimaryIllumination:
          param.illuminationValue = {
            actualForColors: this.formData.illuminationColors ?? undefined,
            actualForWhites: this.formData.illuminationWhites ?? undefined,
          };
          break;
      }

      param.isWithinSpec = this.isParameterWithinSpec(param);

      return param;
    });
  }

  isParameterWithinSpec(param: LabTestingResultParameter): boolean | undefined {
    const expected = param.expectedValue;
    const actual = param.actualValue;

    if (!actual?.value && actual?.value !== '0') {
      return undefined;
    }

    const actualNum = parseFloat(actual.value);
    if (isNaN(actualNum)) {
      return undefined;
    }

    if (expected?.min !== undefined && expected?.max !== undefined) {
      return actualNum >= expected.min && actualNum <= expected.max;
    }

    if (expected?.value) {
      const expectedNum = parseFloat(expected.value);
      if (!isNaN(expectedNum)) {
        return actualNum === expectedNum || Math.abs(actualNum - expectedNum) <= 1;
      }
    }

    return undefined;
  }

  cancelCreate(): void {
    this.isCreating.set(false);
    this.selectedStandardForTest.set(null);
    this.selectedStandard.reset();
    this.setDefaultBreadcrumb();
  }

  saveNewTest(navigateAway = false): void {
    const pc = this.productionCard();
    const standard = this.selectedStandardForTest();
    if (!pc) return;

    const parameters = this.buildParametersFromForm();

    // A parameter with no standard defined for it counts as passing.
    const allParametersPass = parameters.every((p) => p.isWithinSpec === undefined || p.isWithinSpec === true);

    const newTest: LabTestingResult = {
      productionCardReference: pc.id!,
      labTestingStandardReference: standard?.id,
      parameters,
      passed: allParametersPass,
    };

    this.incrementLoading();
    this.labTestingResultProxy
      .create(newTest)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Success', 'Success'),
              this.localeHelperService.translateWithFallback('Lab_Tests_TestCreated', 'Test created successfully'),
            );
            this.loadTestResults(pc.id!);
            if (navigateAway) {
              this.cancelCreate();
            }
          }
        },
      });
  }

  save(navigateAway = false): void {
    if (this.isCreating()) {
      this.saveNewTest(navigateAway);
    }
  }

  cancel(): void {
    this.cancelCreate();
  }

  deleteTest(test: LabTestingResult): void {
    if (!test.id) return;

    this.incrementLoading();
    this.labTestingResultProxy
      .delete(test.id)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('Success', 'Success'),
            this.localeHelperService.translateWithFallback('Lab_Tests_TestDeleted', 'Test deleted'),
          );
          const pc = this.productionCard();
          if (pc?.id) {
            this.loadTestResults(pc.id);
          }
        },
      });
  }

  get isTestAdminUser(): boolean {
    return AuthService.hasRole(FinishingRoles.FinishingAdmin);
  }

  canHardApprove(test: LabTestingResult): boolean {
    if (!this.isTestAdminUser) return false;
    if (test.passed) return false;
    if (test.hardApproval) return false;

    const allTests = this.testResults();
    if (allTests.length === 0) return false;

    // Hard-approve only applies to the most recently created test for this card.
    const sortedTests = [...allTests].sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime());
    return sortedTests[0]?.id === test.id;
  }

  getHardApprovedTooltip(test: LabTestingResult): string {
    return this.localeHelperService.translateWithFallback('Lab_Tests_HardApprovedByUser', 'Hard approved by {{userName}}', {
      userName: test.hardApproval?.userName ?? '',
    });
  }

  getStatusLabel(test: LabTestingResult): string {
    if (test.hardApproval) {
      return this.localeHelperService.translateWithFallback('Lab_Tests_Status_HardApproved', 'HARD APPROVED');
    }
    if (test.passed === true) {
      return this.localeHelperService.translateWithFallback('Lab_Tests_Status_Passed', 'PASSED');
    }
    if (test.passed === false) {
      return this.localeHelperService.translateWithFallback('Lab_Tests_Status_Failed', 'FAILED');
    }
    return this.localeHelperService.translateWithFallback('Lab_Tests_Status_Pending', 'PENDING');
  }

  confirmHardApprove(test: LabTestingResult): void {
    this.confirmationService.confirm({
      message: this.translate.instant('Lab_Tests_HardApprove_ConfirmMessage'),
      header: this.translate.instant('Lab_Tests_HardApprove_ConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('Lab_Tests_HardApprove_Accept'),
      rejectLabel: this.translate.instant('Cancel'),
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => this.executeHardApprove(test),
    });
  }

  private executeHardApprove(test: LabTestingResult): void {
    if (!test.id) return;

    this.incrementLoading();
    this.labTestingResultProxy
      .hardApprove(test.id)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(this.translate.instant('Success'), this.translate.instant('Lab_Tests_HardApprove_Success'));
          const pc = this.productionCard();
          if (pc?.id) {
            this.loadTestResults(pc.id);
          }
        },
      });
  }
}
